import React, { useMemo, useState } from "react";
import { FormControl } from "react-bootstrap";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../designsystem/KC_Button/KC_Button";
import KC_UIText from "../../designsystem/typography/KC_UIText/KC_UIText";
import { fetchPromoCode } from "./api";
import { useAccountToDisplaySelector } from "../../redux/actions/user";
import BaseJSBackendResponse from "../../model/responsemodel/BaseJSBackendResponse";
import ReturnCodes from "../../utils/error_handling/ReturnCodes";
import getErrorMessage from "../../utils/error_handling/getErrorMessage";
import { tt } from "../../utils/i18n/i18n";
import { useHistory } from "react-router-dom";
import SaaSPromoCode from "@tootsweet/model/entities/saas/SaaSPromoCode";

interface Props {
  saasPlanId: string;
  saasCommitment: string;
  subscriptionPromoCode2?: string | null;
  appliedPromoCodeText?: string;
  idSuffix: string;
}

export default function CodePromo(props: Props) {
  const history = useHistory();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [returnCode, setReturnCode] = useState(ReturnCodes.Ok);

  const isAppliedFromUrl = !!props.appliedPromoCodeText;

  const errorData = useMemo(() => {
    return {
      hasError: returnCode !== ReturnCodes.Ok,
      msg: getErrorMessage(returnCode)
    };
  }, [returnCode, code]);

  const handleOpen = () => {
    setReturnCode(ReturnCodes.Ok);
    setCode("");
    setOpen(!open);
  };

  const handleApply = async () => {
    if (!accountToDisplay?.token || !accountToDisplay?.trackingID || !code) {
      return;
    }

    let promoCode: SaaSPromoCode | null = null;

    try {
      promoCode = await fetchPromoCode(
        accountToDisplay?.trackingID,
        code.trim(),
        props.saasPlanId,
        accountToDisplay?.token
      );
      setReturnCode(ReturnCodes.Ok);
    } catch (e) {
      //@ts-ignore
      const payload = e?.response?.data as BaseJSBackendResponse<null>;

      if (payload) {
        setReturnCode(payload.code);
      } else {
        setReturnCode(ReturnCodes.Generic);
      }

      return;
    }

    if (!promoCode) {
      alert(tt("errorAlert"));
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("spc", code.trim());
    history.replace(`?${urlParams.toString()}`);
  };

  const handleReset = () => {
    setCode("");
    setOpen(false);

    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete("spc");
    history.replace(`?${urlParams.toString()}`);
  };

  return (
    <div>
      <div className="d-flex justify-content-start">
        {!open && !isAppliedFromUrl && (
          <KC_Button
            id={`open-promo-code-btn-${props.idSuffix}`}
            size={KC_ButtonSize.Large}
            type={KC_ButtonType.Button}
            isPrimary={false}
            isBlack={true}
            className="text-decoration-none mt-2 p-0"
            onClick={handleOpen}
            text={tt("addPromoCodeButton")}
          />
        )}
      </div>

      {isAppliedFromUrl && (
        <div className="d-flex flex-column">
          <KC_UIText
            weight="reg"
            text={tt("promoCodeAppliedMessage")}
            className="text-success my-2"
          />
        </div>
      )}

      {open && (
        <div>
          <div className="d-flex flex-column text-center">
            <div className="kc-input-wrapper mt-2">
              <FormControl
                id={`promo-code-input-${props.idSuffix}`}
                className="kc_input"
                placeholder={tt("promoCodePlaceholder")}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={isAppliedFromUrl}
              />
            </div>
            <div className="mt-2">
              {!isAppliedFromUrl && (
                <KC_Button
                  id={`apply-promo-code-${props.idSuffix}`}
                  size={KC_ButtonSize.Large}
                  className="border"
                  isPrimary={false}
                  isBlack={!code}
                  isTurquoise={!!code}
                  onClick={handleApply}
                  disabled={!code.trim()}
                  text={tt("apply")}
                  type={KC_ButtonType.Button}
                />
              )}
            </div>

            {errorData.hasError && (
              <KC_UIText
                weight={"reg"}
                text={errorData.msg}
                className="text-danger mt-1"
              />
            )}
          </div>
        </div>
      )}

      {isAppliedFromUrl && (
        <KC_Button
          id="apply2"
          size={KC_ButtonSize.Large}
          type={KC_ButtonType.Button}
          className="text-decoration-none p-0 mt-2"
          isPrimary={false}
          isWhite={true}
          onClick={handleReset}
          text={tt("cancel")}
        />
      )}
    </div>
  );
}
